/**
 * Owner: Masa Bayu
 * File: hub-sync.ts
 * Date:Time: 2026-10-17 23:05
 * Description: Fasa 4 — pelanggan qxp-hub untuk qxread: catalog overlay + harga
 *   SSOT + entitlement token-bound + checkout→bayar + sumber artifak. Fail-soft:
 *   hub luar talian → catalog statik + entitlement setempat kekal berfungsi.
 *
 * Invariat kekal: catalog/UI tidak pernah menggrants akses — grants hanya lahir
 *   daripada checkout yang pembayarannya disahkan provider (POST .../pay) atau
 *   fulfillment admin server, dan disinkron masuk melalui fungsi ini. Sesi auth
 *   diurus oleh hub-auth.ts (token localStorage, status reaktif).
 */

import { applyExternalHubGrants, dropHubSourcedEntitlements } from '../library/demo-library-store';
import {
  invalidateAllLoadedHubArtifacts,
  invalidateLoadedHubArtifacts,
  registerQxpArtifactSource,
} from '../reader/shared/qxp-artifact-registry';
import {
  hubDescriptorToCatalogSerialized,
  parseQxpHubCatalog,
  type QxpHubCatalog,
} from './hub-descriptor';
import { setHubCatalogOverlay } from './catalog-port';
import { setHubPrices } from './hub-pricing';
import { setHubSampleCeilings } from './hub-sample-ceiling';
import { setHubCovers, type HubCoverLocation } from './hub-covers';
import {
  clearHubAuth,
  getHubAuthToken,
  markHubAuthExpired,
  setHubAuth,
} from './hub-auth';

/** Origin hub — default dev tempatan; boleh tindih melalui env VITE_QXP_HUB_ORIGIN. */
export function qxpHubOrigin(): string {
  const fromEnv = import.meta.env.VITE_QXP_HUB_ORIGIN as string | undefined;
  const origin = (fromEnv ?? 'http://127.0.0.1:5177').trim().replace(/\/+$/, '');
  return origin;
}

export interface HubResponse {
  readonly ok: boolean;
  readonly status: number;
  readonly json: () => Promise<unknown>;
}

export interface HubFetchInit {
  readonly method?: string;
  readonly body?: string;
  readonly headers?: Readonly<Record<string, string>>;
}

/** Kontrak suntikan fetch: pulangkan respons mentah (ok/status/json). */
export type HubFetch = (url: string, init?: HubFetchInit) => Promise<HubResponse>;

async function hubFetch(url: string, init?: HubFetchInit): Promise<HubResponse> {
  const res = (await fetch(url, init)) as HubResponse;
  return res;
}

/** Rangkaian rosak → null; respons HTTP (sebarang status) tetap dipulangkan. */
async function safeHubFetch(
  fetchImpl: HubFetch,
  url: string,
  init?: HubFetchInit,
): Promise<HubResponse | null> {
  try {
    return await fetchImpl(url, init);
  } catch {
    return null;
  }
}

const JSON_HEADERS: Readonly<Record<string, string>> = { 'content-type': 'application/json' };

function sessionHeaders(): Record<string, string> | null {
  const token = getHubAuthToken();
  return token === null ? null : { ...JSON_HEADERS, authorization: `Bearer ${token}` };
}

/** Tarik catalog hub → harga SSOT + overlay dinamik + daftarkan sumber artifak. */
export async function syncQxpHubCatalog(
  fetchImpl: HubFetch = hubFetch,
  origin: string = qxpHubOrigin(),
): Promise<number> {
  const res = await safeHubFetch(fetchImpl, `${origin}/v1/catalog`);
  if (res === null || !res.ok) return 0;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return 0;
  }
  const catalog: QxpHubCatalog | null = parseQxpHubCatalog(body);
  if (catalog === null) return 0;
  // Harga hub mesti terisi SEBELUM overlay bump — pelanggan catalog membaca
  // semula listMarketplaceBooks pada notifikasi (harga SSOT server mengatasi demo).
  const prices: Record<
    string,
    { priceMinorUnits: number; currency: string; sampleMaxPage: number | null }
  > = {};
  const covers: Record<string, HubCoverLocation> = {};
  // §510-S2 — bumbung sampel didaftarkan untuk SETIAP descriptor, bebas harga:
  // buku percuma/tanpa harga tetap terikat pada deklarasi penerbit.
  const ceilings: Record<string, number> = {};
  for (const descriptor of catalog.publications) {
    if (descriptor.priceMinorUnits !== null && descriptor.currency !== null) {
      prices[descriptor.publicationId] = {
        priceMinorUnits: descriptor.priceMinorUnits,
        currency: descriptor.currency,
        sampleMaxPage: descriptor.sampleMaxPage,
      };
    }
    if (descriptor.sampleMaxPage !== null) {
      ceilings[descriptor.publicationId] = descriptor.sampleMaxPage;
    }
    // COVER-A3 — fail cover berada di luar `content/` (tiada gate DRM), jadi kad
    // tetamu boleh memuatnya; URL compos dari base artifak versiy descriptor.
    if (descriptor.coverAssetRef !== null && descriptor.coverSource !== null) {
      covers[descriptor.publicationId] = {
        url: `${origin}${descriptor.artifactBaseUrl}/${descriptor.coverAssetRef}`,
        source: descriptor.coverSource,
      };
    }
    registerQxpArtifactSource(descriptor.publicationId, `${origin}${descriptor.artifactBaseUrl}`);
  }
  setHubPrices(prices);
  setHubSampleCeilings(ceilings);
  setHubCovers(covers);
  setHubCatalogOverlay(catalog.publications.map(hubDescriptorToCatalogSerialized));
  return catalog.publications.length;
}

export interface QxpHubGrant {
  readonly grantId: string;
  readonly publicationId: string;
  readonly scope: string;
  readonly state: string;
}

/** Tapis struktur grant hub (lengkap sahaja yang diterima). */
function toHubGrant(raw: unknown): QxpHubGrant | null {
  if (raw === null || typeof raw !== 'object') return null;
  const g = raw as Record<string, unknown>;
  if (
    typeof g.grantId === 'string' &&
    typeof g.publicationId === 'string' &&
    typeof g.scope === 'string' &&
    typeof g.state === 'string'
  ) {
    return {
      grantId: g.grantId,
      publicationId: g.publicationId,
      scope: g.scope,
      state: g.state,
    };
  }
  return null;
}

function projectGrant(grant: QxpHubGrant): void {
  applyExternalHubGrants([grant]);
  // Muatan artifak mungkin sudah tersimpan separa (sampel terpaggar) — buang cache
  // supaya akses penuh dimuat semula dengan Bearer baharu.
  invalidateLoadedHubArtifacts(grant.publicationId);
}

/**
 * §509 F4 — sesi berakhir (pelayan balas 401/403): token dibuang DAN hak + muatan yang
 * lahir daripada sesi itu ikut dibuang. `markHubAuthExpired()` sahaja tidak cukup —
 * `loadedArtifacts` berkunci publicationId tanpa pemilikan sesi, dan proyeksi library
 * tetap `granted`. Semua pemanggil yang mengurus 401 wajib pakai fungsi ini.
 */
export function expireHubSession(): void {
  markHubAuthExpired();
  dropHubSourcedEntitlements();
  invalidateAllLoadedHubArtifacts();
}

/**
 * Log keluar setempat (dipakai `logoutSession()` + panel admin/penerbit + padam akaun).
 * Hub belum ada laluan rampasan sesi (§457 F3), jadi ini HANYA membersihkan peranti —
 * token curang tetap sah di pelayan sehingga TTL.
 */
export function endHubSession(): void {
  clearHubAuth();
  dropHubSourcedEntitlements();
  invalidateAllLoadedHubArtifacts();
}

/**
 * Tarik entitlement akaun daripada hub (token-bound; akaun ditentukan server).
 * Tanpa token → 0 (anonymous kekal pada entitlement setempat). 401 → sesi tamat.
 * §509 F3 — jawapan 200 ialah SNAPSHOT penuh akaun: grant yang hilang daripadanya
 * diturunkan kepada `revoked`, bukan dibiarkan (bacaan lama "device cache ⊃ server
 * truth" membiarkan hak kekal walaupun pelayan sudah menarik balik).
 */
export async function syncQxpHubEntitlements(
  fetchImpl: HubFetch = hubFetch,
  origin: string = qxpHubOrigin(),
): Promise<number> {
  const headers = sessionHeaders();
  if (headers === null) return 0;
  const res = await safeHubFetch(fetchImpl, `${origin}/v1/entitlements`, { headers });
  if (res === null) return 0;
  if (res.status === 401) {
    expireHubSession();
    return 0;
  }
  if (!res.ok) return 0;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return 0;
  }
  if (body === null || typeof body !== 'object') return 0;
  const record = body as { readonly grants?: unknown };
  if (!Array.isArray(record.grants)) return 0;
  const grants: QxpHubGrant[] = [];
  for (const raw of record.grants) {
    const grant = toHubGrant(raw);
    if (grant !== null) grants.push(grant);
  }
  const applied = applyExternalHubGrants(grants, { fullSnapshot: true });
  // §509 F4 — set hak berubah (tambah / turun): muatan lama tidak lagi sepadan
  // dengan sesi → buang supaya permintaan seterusnya melalui gerbang pelayan.
  if (applied > 0) invalidateAllLoadedHubArtifacts();
  return applied;
}

export type QxpHubCheckoutRedirect = {
  readonly status: 'redirect';
  readonly checkoutUrl: string;
};

export type QxpHubPurchaseResult =
  | 'granted'
  | 'auth-required'
  | 'unavailable'
  | QxpHubCheckoutRedirect;

export function isQxpHubCheckoutRedirect(
  result: QxpHubPurchaseResult,
): result is QxpHubCheckoutRedirect {
  return typeof result === 'object';
}

/**
 * 202 daripada hub bermakna bayaran berlaku di halaman Stripe, bukan di sini.
 * URL diterima hanya jika absolute http(s) — respons hub tidak boleh jadi arah
 * ke skema lain (`javascript:` dsb.) yang kemudian `location.assign` telan.
 */
function toCheckoutRedirect(status: number, body: unknown): QxpHubCheckoutRedirect | null {
  if (status !== 202) return null;
  const url = (body as { readonly checkoutUrl?: unknown } | null)?.checkoutUrl;
  if (typeof url !== 'string' || url.length === 0) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
  } catch {
    return null;
  }
  return { status: 'redirect', checkoutUrl: url };
}

/**
 * Beli melalui hub (SSOT): checkout → bayar melalui provider.
 * 'granted' — grant aktif diterima / sudah dimiliki (409 checkout mengesahkan
 *   pemilikan; entitlements disinkron semula untuk memastikan proyeksi tepat).
 * 'auth-required' — tiada sesi / sesi ditolak server (401); UI meminta daftar masuk.
 * {status:'redirect'} — provider Stripe membuka Checkout Session (202): tugas UI
 *   hanya menghantar pembeli ke halaman Stripe. Order masih `pending` dan TIADA
 *   grant sehingga webhook bertandatangan sampai; keberangkatan ke URL ini BUKAN
 *   bukti bayaran.
 * 'unavailable' — hub luar talian, terbitan tiada/tiada harga (422/404), atau
 *   pembayaran tidak disahkan (402).
 */
export async function purchaseViaQxpHub(
  publicationId: string,
  fetchImpl: HubFetch = hubFetch,
  origin: string = qxpHubOrigin(),
): Promise<QxpHubPurchaseResult> {
  const id = publicationId.trim();
  const headers = sessionHeaders();
  if (headers === null) return 'auth-required';

  const checkout = await safeHubFetch(fetchImpl, `${origin}/v1/checkout`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ publicationId: id }),
  });
  if (checkout === null) return 'unavailable';
  if (checkout.status === 401) {
    expireHubSession();
    return 'auth-required';
  }
  if (!checkout.ok) {
    if (checkout.status === 409) {
      await syncQxpHubEntitlements(fetchImpl, origin);
      return 'granted';
    }
    return 'unavailable';
  }
  let checkoutBody: unknown;
  try {
    checkoutBody = await checkout.json();
  } catch {
    return 'unavailable';
  }
  const orderId = (checkoutBody as { readonly order?: { readonly orderId?: unknown } }).order
    ?.orderId;
  if (typeof orderId !== 'string') return 'unavailable';

  const pay = await safeHubFetch(fetchImpl, `${origin}/v1/checkout/${orderId}/pay`, {
    method: 'POST',
    headers,
  });
  if (pay === null) return 'unavailable';
  if (pay.status === 401) {
    expireHubSession();
    return 'auth-required';
  }
  // Body dibaca dahulu: 202 (Stripe Checkout) juga membawa JSON dan bukan satu ralat.
  let payBody: unknown;
  try {
    payBody = await pay.json();
  } catch {
    return 'unavailable';
  }
  const redirect = toCheckoutRedirect(pay.status, payBody);
  if (redirect !== null) return redirect;
  if (!pay.ok) return 'unavailable';
  const result = payBody as { readonly ok?: unknown; readonly grant?: unknown };
  if (result.ok !== true) return 'unavailable';
  const grant = toHubGrant(result.grant);
  if (grant !== null) projectGrant(grant);
  return 'granted';
}

export type QxpHubAuthAttempt =
  | { readonly status: 'signed-in' }
  | { readonly status: 'error'; readonly error: 'credentials' | 'invalid' | 'unavailable' };

/** Daftar masuk akaun hub sedia ada. */
export async function loginQxreadHubAccount(
  email: string,
  password: string,
  fetchImpl: HubFetch = hubFetch,
  origin: string = qxpHubOrigin(),
): Promise<QxpHubAuthAttempt> {
  const res = await safeHubFetch(fetchImpl, `${origin}/v1/accounts/login`, {
    method: 'POST',
    headers: { ...JSON_HEADERS },
    body: JSON.stringify({ email: email.trim(), password }),
  });
  if (res === null) return { status: 'error', error: 'unavailable' };
  if (res.status === 401) return { status: 'error', error: 'credentials' };
  if (res.status === 422) return { status: 'error', error: 'invalid' };
  if (!res.ok) return { status: 'error', error: 'unavailable' };
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { status: 'error', error: 'unavailable' };
  }
  const record = body as {
    readonly ok?: unknown;
    readonly token?: unknown;
    readonly accountId?: unknown;
  };
  if (
    record.ok !== true ||
    typeof record.token !== 'string' ||
    typeof record.accountId !== 'string'
  ) {
    return { status: 'error', error: 'unavailable' };
  }
  setHubAuth(record.token, record.accountId);
  return { status: 'signed-in' };
}

/** Satu panggilan permulaan: catalog + entitlement (token-bound). Fire-and-forget selamat. */
export async function syncQxpHubState(
  fetchImpl: HubFetch = hubFetch,
): Promise<{ catalogCount: number; grantsApplied: number }> {
  const catalogCount = await syncQxpHubCatalog(fetchImpl);
  const grantsApplied = await syncQxpHubEntitlements(fetchImpl);
  return { catalogCount, grantsApplied };
}

// §422 K1 — deep link /read/ mesti menunggu sync pertama selesai sebelum memutuskan
// sama ada satu publication mempunyai sumber artifak (fallback statik sudah dibuang).
let initialHubSync: Promise<{ catalogCount: number; grantsApplied: number }> | null = null;

export function ensureQxpHubSynced(): Promise<{ catalogCount: number; grantsApplied: number }> {
  initialHubSync ??= syncQxpHubState();
  return initialHubSync;
}

/** Selepas publish berjaya — katalog dan hak dibaca semula, bukan cache mula. */
export function resyncQxpHub(): Promise<{ catalogCount: number; grantsApplied: number }> {
  initialHubSync = syncQxpHubState();
  return initialHubSync;
}
